import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../hooks/useAuth';
import './Hub.css';

function Hub() {
  const navigate = useNavigate();
  const { currentUser, login, logout } = useAuth();
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [stats, setStats] = useState({ agreements: 0, customers: 0, employees: 0, totalValue: 0 });
  const [time, setTime] = useState(new Date());

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch stats after login
  useEffect(() => {
    if (!currentUser) return;
    const fetchStats = async () => {
      const [{ count: agCount }, { count: custCount }, { count: empCount }, { data: totals }] = await Promise.all([
        supabase.from('agreements').select('*', { count: 'exact', head: true }),
        supabase.from('customers').select('*', { count: 'exact', head: true }),
        supabase.from('employees').select('*', { count: 'exact', head: true }),
        supabase.from('agreements').select('total'),
      ]);
      const totalValue = (totals || []).reduce((sum, a) => sum + parseFloat(a.total || 0), 0);
      setStats({
        agreements: agCount || 0,
        customers: custCount || 0,
        employees: empCount || 0,
        totalValue,
      });
    };
    fetchStats();
  }, [currentUser]);

  const handleLogin = async () => {
    setLoginLoading(true);
    setError('');
    const result = await login(loginData.username, loginData.password);
    if (!result.success) {
      setError('Invalid username or password. Access denied.');
    }
    setLoginLoading(false);
  };

  const handleLock = () => {
    logout();
  };

  const formatTime = (date) =>
    date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const formatDate = (date) =>
    date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const formatValue = (val) =>
    val >= 1000000
      ? `AED ${(val / 1000000).toFixed(2)}M`
      : val >= 1000
      ? `AED ${(val / 1000).toFixed(1)}K`
      : `AED ${val.toFixed(0)}`;

  // ── LOGIN ──
  if (!currentUser) {
    return (
      <div className="hub-login">
        <div className="hub-login-bg" />
        <div className="hub-login-card">
          <div className="hub-login-emblem">
            <span>🏛️</span>
          </div>
          <p className="hub-login-brand">AL MUSHTARAKA TRADING CO.</p>
          <h2 className="hub-login-title">ADMIN GATEWAY</h2>
          <p className="hub-login-sub">Restricted access. Authorised personnel only.</p>

          <div className="hub-input-wrapper">
            <span className="hub-input-icon">👤</span>
            <input
              type="text"
              placeholder="Username"
              value={loginData.username}
              onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              autoFocus
            />
          </div>

          <div className="hub-input-wrapper">
            <span className="hub-input-icon">🔑</span>
            <input
              type={showPass ? 'text' : 'password'}
              placeholder="Password"
              value={loginData.password}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
            <button className="hub-toggle-pass" onClick={() => setShowPass(p => !p)} tabIndex={-1}>
              {showPass ? '🙈' : '👁️'}
            </button>
          </div>

          {error && <p className="hub-login-error">⚠️ {error}</p>}

          <button className="hub-login-btn" onClick={handleLogin} disabled={loginLoading}>
            {loginLoading ? 'Unlocking...' : 'Unlock Gateway →'}
          </button>

          <p className="hub-login-footer">Al Mushtaraka Trading Company · Dibba Industrial Area, UAE</p>
        </div>
      </div>
    );
  }

  // ── DASHBOARD ──
  const modules = [
    {
      key: 'agreement',
      icon: '📋',
      title: 'Agreement Module',
      description: 'Create, manage and generate Sale & Lease Cum Sale agreements with digital signatures.',
      stat: stats.agreements,
      statLabel: 'Total Agreements',
      accent: '#c8a96e',
      path: '/agreement',
    },
    {
      key: 'customers',
      icon: '🏢',
      title: 'Customer Module',
      description: 'Add and manage customer records, ID documents, partners and financial details.',
      stat: stats.customers,
      statLabel: 'Total Customers',
      accent: '#4caf82',
      path: '/customers',
    },
    {
      key: 'admin',
      icon: '🔐',
      title: 'Admin Panel',
      description: 'View all agreement history, manage employees, access verification photos and signatures.',
      stat: stats.employees,
      statLabel: 'Active Employees',
      accent: '#7b8cde',
      path: '/admin',
      adminOnly: true,
    },
  ].filter((mod) => !mod.adminOnly || currentUser?.role === 'admin');

  return (
    <div className="hub-dashboard">
      {/* Background layers */}
      <div className="hub-bg-grid" />
      <div className="hub-bg-glow" />

      {/* Top bar */}
      <header className="hub-topbar">
        <div className="hub-topbar-left">
          <div className="hub-topbar-logo">AM</div>
          <div>
            <p className="hub-topbar-company">AL MUSHTARAKA TRADING COMPANY</p>
            <p className="hub-topbar-location">Dibba Industrial Area, UAE</p>
          </div>
        </div>
        <div className="hub-topbar-center">
          <p className="hub-clock">{formatTime(time)}</p>
          <p className="hub-date">{formatDate(time)}</p>
        </div>
        <div className="hub-topbar-right">
          <button className="hub-logout-btn" onClick={handleLock}>
            🔒 Lock Gateway
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="hub-hero">
        <p className="hub-hero-eyebrow">Admin Gateway · All Systems</p>
        <h1 className="hub-hero-title">Command Centre</h1>
        <p className="hub-hero-sub">Select a module to access its full functionality.</p>
      </section>

      {/* Stats strip */}
      <div className="hub-stats-strip">
        <div className="hub-stat">
          <span className="hub-stat-value">{stats.agreements}</span>
          <span className="hub-stat-label">Agreements</span>
        </div>
        <div className="hub-stat-divider" />
        <div className="hub-stat">
          <span className="hub-stat-value">{stats.customers}</span>
          <span className="hub-stat-label">Customers</span>
        </div>
        <div className="hub-stat-divider" />
        <div className="hub-stat">
          <span className="hub-stat-value">{stats.employees}</span>
          <span className="hub-stat-label">Employees</span>
        </div>
        <div className="hub-stat-divider" />
        <div className="hub-stat">
          <span className="hub-stat-value">{formatValue(stats.totalValue)}</span>
          <span className="hub-stat-label">Total Value</span>
        </div>
      </div>

      {/* Module cards */}
      <div className="hub-modules">
        {modules.map((mod) => (
          <div
            key={mod.key}
            className="hub-module-card"
            style={{ '--mod-accent': mod.accent }}
            onClick={() => navigate(mod.path)}
          >
            <div className="hub-module-glow" />
            <div className="hub-module-top">
              <div className="hub-module-icon">{mod.icon}</div>
              <div className="hub-module-stat-box">
                <span className="hub-module-stat-num">{mod.stat}</span>
                <span className="hub-module-stat-lbl">{mod.statLabel}</span>
              </div>
            </div>
            <h3 className="hub-module-title">{mod.title}</h3>
            <p className="hub-module-desc">{mod.description}</p>
            <div className="hub-module-footer">
              <span className="hub-module-access">Access Module →</span>
              <div className="hub-module-indicator" />
            </div>
          </div>
        ))}
      </div>

      <footer className="hub-footer">
        <span>© {new Date().getFullYear()} Al Mushtaraka Trading Company · All rights reserved</span>
      </footer>
    </div>
  );
}

export default Hub;
